export const defaultDashboardSummary = {
  totalServes: 0,
  usage: {
    freeLimit: 5,
    completedAnalyses: 0,
    freeRemaining: 5,
    hasUnlimitedAccess: false,
    hasSubscriptionAccess: false,
    subscriptionStatus: null,
  },
  repeatability: {
    minimumSessions: 5,
    sessionCount: 0,
    isReady: false,
    metrics: [],
  },
  metrics: [
    { label: "Knee load depth", value: 112, unit: "°", detail: "average deepest bend" },
    { label: "Toss alignment", value: 88, unit: "%", detail: "completed serves on track" },
    { label: "Shoulder coil", value: 82, unit: "%", detail: "completed serves on track" },
    { label: "Shoulder uncoil", value: 91, unit: "%", detail: "completed serves on track" },
    { label: "Follow-through arc", value: 84, unit: "%", detail: "completed serves on track" },
    { label: "Forward landing", value: 90, unit: "%", detail: "completed serves on track" },
  ],
  focusAreas: [
    { name: "Contact timing", score: 94, tone: "good" },
    { name: "Racket drop depth", score: 82, tone: "watch" },
    { name: "Recovery posture", score: 88, tone: "good" },
  ],
  recentSessions: [
    { title: "Morning Serve Block", date: "Today · 7:15 AM", score: 91, tag: "Ready" },
    { title: "Match Replay", date: "Yesterday · 8:40 PM", score: 87, tag: "Review" },
    { title: "Practice Session", date: "Mon · 6:10 PM", score: 84, tag: "Improving" },
  ],
  progressHistory: [
    { title: "Session 1", date: "Mon", score: 74 },
    { title: "Session 2", date: "Wed", score: 81 },
    { title: "Session 3", date: "Today", score: 88 },
  ],
  serveFlow: [
    { label: "Stance", detail: "Balanced base", tone: "good" },
    { label: "Toss", detail: "On target", tone: "good" },
    { label: "Coil", detail: "Build the coil", tone: "watch" },
    { label: "Trophy", detail: "Aligned", tone: "good" },
    { label: "Knee load", detail: "112° bend", tone: "good" },
    { label: "Uncoil", detail: "Create more drive", tone: "watch" },
    { label: "Finish", detail: "Complete arc", tone: "good" },
    { label: "Landing", detail: "Forward landing", tone: "good" },
  ],
  metricTrends: [],
};

defaultDashboardSummary.latestServeFlow = defaultDashboardSummary.serveFlow;
defaultDashboardSummary.averageServeFlow = defaultDashboardSummary.serveFlow;

// The production contract should map these cards to the existing analytics tables:
// - serve_summary_v1_mat / serve_summary
// - serve_summary_points_front_end
// - serve_feedback_structured
// This fallback intentionally keeps the UI working until the backend contract is live.
export function normalizeDashboardSummary(payload = {}) {
  const source = payload && typeof payload === "object" ? payload : {};
  const totalServes = typeof source.totalServes === "number" ? source.totalServes : Number(source.totalServes) || 0;
  const metrics = Array.isArray(source.metrics) ? source.metrics : defaultDashboardSummary.metrics;
  const focusAreas = Array.isArray(source.focusAreas) ? source.focusAreas : defaultDashboardSummary.focusAreas;
  const recentSessions = Array.isArray(source.recentSessions) ? source.recentSessions : defaultDashboardSummary.recentSessions;
  const progressHistory = Array.isArray(source.progressHistory) ? source.progressHistory : defaultDashboardSummary.progressHistory;
  const serveFlow = Array.isArray(source.serveFlow) ? source.serveFlow : defaultDashboardSummary.serveFlow;
  const normalizeServeFlow = (flow) => flow.map((item) => ({
    label: item.label || "Serve phase",
    detail: item.detail || "Measured",
    tone: ["watch", "needs-work", "neutral"].includes(item.tone) ? item.tone : "good",
    evidence: item.evidence || "This phase is derived from the analyzed camera-view motion.",
  }));
  const rawUsage = source.usage && typeof source.usage === "object" ? source.usage : {};
  const rawRepeatability = source.repeatability && typeof source.repeatability === "object" ? source.repeatability : {};
  const freeLimit = Number(rawUsage.freeLimit) || defaultDashboardSummary.usage.freeLimit;
  const hasUnlimitedAccess = rawUsage.hasUnlimitedAccess === true;
  const hasSubscriptionAccess = rawUsage.hasSubscriptionAccess === true;
  const freeRemaining = hasUnlimitedAccess || hasSubscriptionAccess
    ? null
    : Math.max(0, Math.min(freeLimit, Number(rawUsage.freeRemaining)));

  return {
    totalServes,
    usage: {
      freeLimit,
      completedAnalyses: Math.max(0, Number(rawUsage.completedAnalyses) || totalServes),
      freeRemaining: Number.isFinite(freeRemaining) ? freeRemaining : Math.max(0, freeLimit - totalServes),
      hasUnlimitedAccess,
      hasSubscriptionAccess,
      subscriptionStatus: typeof rawUsage.subscriptionStatus === "string" ? rawUsage.subscriptionStatus : null,
    },
    repeatability: {
      minimumSessions: Number(rawRepeatability.minimumSessions) || defaultDashboardSummary.repeatability.minimumSessions,
      sessionCount: Math.max(0, Number(rawRepeatability.sessionCount) || 0),
      isReady: rawRepeatability.isReady === true,
      metrics: Array.isArray(rawRepeatability.metrics)
        ? rawRepeatability.metrics
          .filter((metric) => Number.isFinite(Number(metric.value)))
          .map((metric) => ({
            label: metric.label || "Measured cue",
            value: Number(metric.value),
            unit: metric.unit || "",
          }))
        : [],
    },
    metrics: metrics.map((item) => ({
      label: item.label || "Metric",
      value: typeof item.value === "number" ? item.value : Number(item.value) || 0,
      unit: item.unit || "%",
      detail: item.detail || "completed serves on track",
      tone: ["good", "watch", "needs-work"].includes(item.tone) ? item.tone : "neutral",
    })),
    focusAreas: focusAreas.map((item) => ({
      name: item.name || "Focus area",
      score: typeof item.score === "number" ? item.score : Number(item.score) || 0,
      tone: item.tone === "watch" ? "watch" : "good",
    })),
    recentSessions: recentSessions.map((item) => ({
      title: item.title || "Session",
      date: item.date || "Recent",
      score: typeof item.score === "number" ? item.score : Number(item.score) || 0,
      tag: item.tag || "Ready",
    })),
    progressHistory: progressHistory.map((item) => ({
      title: item.title || "Session",
      date: item.date || "Recent",
      score: typeof item.score === "number" ? item.score : Number(item.score) || 0,
    })),
    serveFlow: normalizeServeFlow(serveFlow),
    latestServeFlow: normalizeServeFlow(
      Array.isArray(source.latestServeFlow) ? source.latestServeFlow : serveFlow
    ),
    averageServeFlow: normalizeServeFlow(
      Array.isArray(source.averageServeFlow) ? source.averageServeFlow : serveFlow
    ),
    metricTrends: Array.isArray(source.metricTrends) ? source.metricTrends : [],
  };
}

export async function fetchDashboardSummary(idToken) {
  try {
    const headers = { "Content-Type": "application/json" };
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`;
    }

    const response = await fetch("/api/dashboard-summary", {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Dashboard API unavailable (${response.status})`);
    }

    const payload = await response.json();
    return normalizeDashboardSummary(payload);
  } catch (error) {
    console.warn("Using local dashboard fallback because no analytics endpoint is active yet.", error);
    return normalizeDashboardSummary(defaultDashboardSummary);
  }
}
